import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

/** Merge Tailwind classes with conflict resolution */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/** Format KES currency — e.g., "KES 45,000" */
export function formatKES(amount: number): string {
  return `KES ${amount.toLocaleString("en-KE")}`;
}

/** Format short KES — e.g., "45K" or "1.2M" */
export function formatKESShort(amount: number): string {
  if (amount >= 1_000_000) return `${(amount / 1_000_000).toFixed(1)}M`;
  if (amount >= 1_000) return `${(amount / 1_000).toFixed(0)}K`;
  return amount.toString();
}

/** Format relative time — e.g., "2m ago", "3h ago" */
export function timeAgo(timestamp: number): string {
  const seconds = Math.floor((Date.now() - timestamp) / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

/** Format time — e.g., "14:32" */
export function formatTime(timestamp: number): string {
  return new Date(timestamp).toLocaleTimeString("en-KE", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

/** Format date — e.g., "17 Mar 2026" */
export function formatDate(timestamp: number | string): string {
  return new Date(timestamp).toLocaleDateString("en-KE", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/** Truncate text to max length with ellipsis */
export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 1) + "…";
}

/** Generate initials from a name — e.g., "John Doe" → "JD" */
export function initials(name: string): string {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

/** Status color mapping */
export function statusColor(
  status: "active" | "pending" | "error" | "warning" | "inactive",
): string {
  const map: Record<string, string> = {
    active: "text-success",
    pending: "text-warning",
    error: "text-error",
    warning: "text-warning",
    inactive: "text-muted-foreground",
  };
  return map[status] ?? "text-muted-foreground";
}

/** Status badge color */
export function statusBadge(
  status: "active" | "pending" | "error" | "warning" | "inactive",
): string {
  const map: Record<string, string> = {
    active: "bg-success/10 text-success",
    pending: "bg-warning/10 text-warning",
    error: "bg-error/10 text-error",
    warning: "bg-warning/10 text-warning",
    inactive: "bg-muted text-muted-foreground",
  };
  return map[status] ?? "bg-muted text-muted-foreground";
}
